import React, { useEffect } from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import {
  BaseCard,
  BaseCardHeader,
  Filter,
  SkeletonWrapper,
  Tag,
} from '@neteera/neteera-web-nextgen';

import { deepEqual } from 'src/utils/comparators';
import {
  generate12MonthOptions,
  getMonitoringDaysTrendColor,
  getMonitoringDaysTrendIconProps,
} from 'src/routes/IndividualDashboard/modules/dashboard/utils';
import Connector, { PropsFromRedux } from './Connector';
import { Wrapper } from './styled';

type Props = PropsFromRedux & {
  intl: IntlShape;
};

const options = generate12MonthOptions();

const MonitoringDays = ({
  intl,
  isLoading,
  monitoringDaysCount,
  totalDays,
  trend,
  patientId,
  getMonitoringDaysData,
}: Props) => {
  const [activeOption, setActiveOption] = React.useState<Record<
    'label' | 'value',
    string
  > | null>(options[0] || null);
  const monitoredValue =
    Number.isInteger(monitoringDaysCount) && Number.isInteger(totalDays)
      ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${monitoringDaysCount}/${totalDays}`
      : intl.formatMessage(messages.na);

  useEffect(() => {
    if (patientId && activeOption) {
      getMonitoringDaysData({
        patientId,
        currentMonth: activeOption.value,
      });
    }
  }, [patientId, getMonitoringDaysData, activeOption]);

  return (
    <BaseCard className="pt-1">
      <BaseCardHeader
        title={intl.formatMessage(messages.monitoringDays)}
        leftIconProps={{ iconName: 'Goal' }}
        size="base"
      >
        <Filter
          triggerContent="Select Month"
          options={options.map(option => ({
            ...option,
            disabled: activeOption?.value === option.value,
          }))}
          onOptionSelect={setActiveOption}
          defaultValue={activeOption?.value}
          triggerContentClassName="bg-neteera-teal-200 h-full py-3 px-1 pl-2"
        />
      </BaseCardHeader>
      <SkeletonWrapper isLoading={isLoading} full className="mt-2">
        <Wrapper>
          <span className="text-xl font-normal">{monitoredValue}</span>
          {Number.isInteger(monitoringDaysCount) &&
          activeOption?.value !== options[0]?.value ? (
            <Tag
              label={`${trend}%`}
              bgColor={getMonitoringDaysTrendColor(
                trend,
                monitoringDaysCount || 0,
              )}
              iconProps={getMonitoringDaysTrendIconProps(trend)}
              size="sm"
            />
          ) : null}
        </Wrapper>
      </SkeletonWrapper>
    </BaseCard>
  );
};

const messages = defineMessages({
  monitoringDays: {
    id: 'MonitoringDays.index.monitoringDays',
    defaultMessage: 'Monitoring Days',
  },
  na: {
    id: 'MonitoringDays.index.na',
    defaultMessage: 'N/A',
  },
});

export default Connector(
  injectIntl(
    React.memo(MonitoringDays, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
